import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { Check, Search, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AdminDataGrid } from '@/components/grid/AdminDataGrid'
import { PaginationControls } from '@/components/grid/PaginationControls'
import { AppShell } from '@/components/layout/AppShell'
import { useAuth } from '@/hooks/use-auth'
import { cn, formatDate, timeAgo } from '@/lib/utils'
import { useWaitlistStore } from '@/stores/waitlist-store'

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'unconfirmed', label: 'Unconfirmed' },
]

const statusStyles: Record<string, string> = {
  pending: 'bg-warning/10 text-warning border-warning/20',
  approved: 'bg-positive/10 text-positive border-positive/20',
  rejected: 'bg-negative/10 text-negative border-negative/20',
  unconfirmed: 'bg-ink-faint/10 text-ink-faint border-ink-faint/20',
}

type WaitlistRow = ReturnType<typeof useWaitlistStore.getState>['entries'][number]
type WaitlistCellParams = ICellRendererParams<WaitlistRow>

export function BetaList() {
  useAuth()
  const {
    entries,
    pagination,
    stats,
    statusFilter,
    selectedIds,
    isLoading,
    setStatusFilter,
    setSearchQuery,
    toggleSelection,
    selectAll,
    clearSelection,
    fetchEntries,
    fetchStats,
    updateStatus,
    batchUpdate,
    deleteEntry,
  } = useWaitlistStore()

  const [searchInput, setSearchInput] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  useEffect(() => {
    fetchEntries()
    fetchStats()
  }, [fetchEntries, fetchStats])

  const handleSearch = () => {
    setSearchQuery(searchInput)
  }

  const allSelected = entries.length > 0 && selectedIds.size === entries.length

  const entryColumns: Array<ColDef<WaitlistRow>> = [
    {
      headerName: '',
      minWidth: 56,
      maxWidth: 56,
      pinned: 'left',
      lockPinned: true,
      resizable: false,
      cellRenderer: (params: WaitlistCellParams) => {
        const data = params.data
        return data ? (
          <input
            type="checkbox"
            checked={selectedIds.has(data.id)}
            onChange={() => toggleSelection(data.id)}
            onClick={(event) => event.stopPropagation()}
            className="h-3.5 w-3.5 rounded border-border accent-accent"
          />
        ) : null
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: WaitlistCellParams) =>
        params.data ? <span className="font-mono text-xs text-ink">{params.data.name}</span> : null,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.2,
      minWidth: 220,
      cellRenderer: (params: WaitlistCellParams) =>
        params.data ? (
          <span className="font-mono text-xs text-ink-muted">{params.data.email}</span>
        ) : null,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      maxWidth: 160,
      cellRenderer: (params: WaitlistCellParams) =>
        params.data ? (
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-medium',
              statusStyles[params.data.status] ?? 'bg-surface-alt text-ink-faint'
            )}
          >
            {params.data.status}
          </span>
        ) : null,
    },
    {
      field: 'source',
      headerName: 'Source',
      minWidth: 140,
      maxWidth: 160,
      cellRenderer: (params: WaitlistCellParams) =>
        params.data ? (
          <span className="font-mono text-[11px] text-ink-faint">{params.data.source ?? '—'}</span>
        ) : null,
    },
    {
      field: 'createdAt',
      headerName: 'Submitted',
      minWidth: 140,
      maxWidth: 160,
      cellRenderer: (params: WaitlistCellParams) =>
        params.data ? (
          <span
            className="font-mono text-[11px] text-ink-faint"
            title={formatDate(params.data.createdAt)}
          >
            {timeAgo(params.data.createdAt)}
          </span>
        ) : null,
    },
    {
      headerName: 'Actions',
      minWidth: 200,
      maxWidth: 220,
      cellRenderer: (params: WaitlistCellParams) => {
        const data = params.data
        if (!data) return null

        return (
          <div className="flex items-center justify-end gap-1.5">
            {data.status === 'pending' ? (
              <>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    updateStatus(data.id, 'approved')
                  }}
                  className="rounded p-1 text-positive transition-colors hover:bg-positive/10"
                  title="Approve"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    updateStatus(data.id, 'rejected')
                  }}
                  className="rounded p-1 text-negative transition-colors hover:bg-negative/10"
                  title="Reject"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </>
            ) : null}
            {confirmDelete === data.id ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    deleteEntry(data.id)
                    setConfirmDelete(null)
                  }}
                  className="rounded bg-negative/10 px-2 py-0.5 font-mono text-[10px] text-negative hover:bg-negative/20"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    setConfirmDelete(null)
                  }}
                  className="font-mono text-[10px] text-ink-faint hover:text-ink-muted"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  setConfirmDelete(data.id)
                }}
                className="rounded p-1 text-ink-faint transition-colors hover:bg-negative/10 hover:text-negative"
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <AppShell title="Beta Waitlist">
      <div className="space-y-5">
        {/* Stats row */}
        {stats && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: 'Total', value: stats.total },
              { label: 'Pending', value: stats.pending },
              { label: 'Approved', value: stats.approved },
              { label: 'Rejected', value: stats.rejected },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-lg border border-dashed border-border bg-surface px-4 py-3"
              >
                <p className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                  {s.label}
                </p>
                <p className="mt-1 font-mono text-lg font-semibold tabular-nums text-ink">
                  {s.value}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-md border border-dashed border-border bg-surface">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={cn(
                  'px-3 py-2 font-mono text-[11px] transition-colors first:rounded-l-md last:rounded-r-md',
                  statusFilter === tab.value
                    ? 'bg-accent/10 text-accent'
                    : 'text-ink-muted hover:bg-surface-alt'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search name or email..."
                className="w-56 rounded-md border border-dashed border-border bg-surface py-2 pl-8 pr-3 font-mono text-[11px] text-ink placeholder:text-ink-faint"
              />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              className="rounded-md border border-dashed border-border bg-surface px-3 py-2 font-mono text-[11px] text-ink-muted transition-colors hover:bg-surface-alt"
            >
              Search
            </button>
          </div>

          {/* Bulk actions */}
          <div className="ml-auto flex items-center gap-2">
            {entries.length > 0 ? (
              <button
                type="button"
                onClick={() => (allSelected ? clearSelection() : selectAll())}
                className="rounded-md border border-dashed border-border bg-surface px-3 py-2 font-mono text-[11px] text-ink-muted transition-colors hover:bg-surface-alt"
              >
                {allSelected ? 'Clear Page' : 'Select Page'}
              </button>
            ) : null}
            {selectedIds.size > 0 ? (
              <>
                <span className="font-mono text-[11px] text-ink-muted">
                  {selectedIds.size} selected
                </span>
                <button
                  type="button"
                  onClick={() => batchUpdate('approved')}
                  className="flex items-center gap-1 rounded-md bg-positive/10 px-3 py-1.5 font-mono text-[11px] text-positive transition-colors hover:bg-positive/20"
                >
                  <Check className="h-3 w-3" /> Approve
                </button>
                <button
                  type="button"
                  onClick={() => batchUpdate('rejected')}
                  className="flex items-center gap-1 rounded-md bg-negative/10 px-3 py-1.5 font-mono text-[11px] text-negative transition-colors hover:bg-negative/20"
                >
                  <X className="h-3 w-3" /> Reject
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="font-mono text-[11px] text-ink-faint hover:text-ink-muted"
                >
                  Clear
                </button>
              </>
            ) : null}
          </div>
        </div>

        <AdminDataGrid
          columns={entryColumns}
          emptyMessage="No entries found"
          getRowClass={({ data }) =>
            data && selectedIds.has(data.id) ? 'lookbook-grid-row-selected' : undefined
          }
          getRowId={({ data }) => data.id}
          loading={isLoading}
          rows={entries}
        />

        <PaginationControls
          label="entries"
          onNext={() => fetchEntries(pagination.page + 1)}
          onPrevious={() => fetchEntries(pagination.page - 1)}
          page={pagination.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
        />
      </div>
    </AppShell>
  )
}
